import type React from "react"
import { useTranslation } from "react-i18next"
import { Link as RouterLink, useMatch } from "react-router"

import { SitePath } from "@/models/route"
import { useFlags } from "@/services/feature"
import { useUrl } from "@/utils/url"

import List from "@mui/material/List"
import ListItemButton from "@mui/material/ListItemButton"
import type { ListItemButtonProps } from "@mui/material/ListItemButton"
import ListItemText from "@mui/material/ListItemText"
import { styled } from "@mui/material/styles"

// Must cast to ListItemButton to avoid type error
// See https://mui.com/material-ui/guides/typescript/#complications-with-the-component-prop
const StyledListItemButton = styled((props: ListItemButtonProps) => (
  <ListItemButton {...props} />
))(({ theme }) => ({
  borderRadius: theme.spacing(0.5),
})) as unknown as typeof ListItemButton

interface SiteNavItem {
  isSelected: boolean
  label: string
  to: string
}

export const SiteSecondaryNav: React.FC = () => {
  const { t } = useTranslation("sites")
  const { buildSiteUrl } = useUrl()
  const { site_bills, site_interval_data } = useFlags()

  const overviewMatch = useMatch(buildSiteUrl(SitePath.Site, SitePath.Overview))
  const detailsMatch = useMatch(
    buildSiteUrl(SitePath.Site, SitePath.SiteDetails)
  )
  const billsMatch = useMatch({
    end: false,
    path: buildSiteUrl(SitePath.Site, SitePath.Bills),
  })
  const intervalsMatch = useMatch(
    buildSiteUrl(SitePath.Site, SitePath.Intervals)
  )

  const showBills = Boolean(site_bills)
  const showIntervals = Boolean(site_interval_data)

  const navItems: SiteNavItem[] = [
    {
      isSelected: Boolean(overviewMatch),
      label: t("site.tab.overview"),
      to: SitePath.Overview,
    },
    {
      isSelected: Boolean(detailsMatch),
      label: t("site.tab.details"),
      to: SitePath.SiteDetails,
    },
    ...(showBills
      ? [
          {
            isSelected: Boolean(billsMatch),
            label: t("site.tab.bills"),
            to: SitePath.Bills,
          },
        ]
      : []),
    ...(showIntervals
      ? [
          {
            isSelected: Boolean(intervalsMatch),
            label: t("site.tab.intervals"),
            to: SitePath.Intervals,
          },
        ]
      : []),
  ]

  return (
    <List
      aria-label={t("site.tab.label")}
      component="nav"
      sx={{ mb: { xs: 2, md: 0 }, mr: { xs: 0, md: 4 }, py: 0 }}
    >
      {navItems.map((item) => (
        <StyledListItemButton
          aria-current={item.isSelected ? "page" : undefined}
          component={RouterLink}
          key={item.to}
          selected={item.isSelected}
          to={item.to}
        >
          <ListItemText primary={item.label} />
        </StyledListItemButton>
      ))}
    </List>
  )
}
